/*
  Pure helpers for the Tasks page (no React). Kept separate so the board, table, calendar, card and task
  pane read a task the same way, and so the rules are unit-tested (src/__tests__/tasks-board.test.js,
  src/__tests__/tasks-w2.test.js).

  Status / priority / due colours follow the app's status language (lib/status.js):
    Open gray · In Progress blue · Blocked red · Done navy — always an icon + a word, never colour alone.
    Priority: High red · Medium amber · Low gray.  Due: "Overdue · 26 Sep" red, "Today" amber, else plain.
*/
import { ageDays, formatDate, toDateInputValue } from '@/lib/format';
import { getStatusTone, TONE_TEXT_CLASS } from '@/lib/status';

export const STATUS_OPTIONS = ['Open', 'In Progress', 'Blocked', 'Cancelled', 'Done'];
export const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];
export const NO_SECTION = 'No Section';

/** 'in_progress' / 'In-Progress' / 'canceled' → 'In Progress' / 'Cancelled'; anything unknown → 'Open'. */
export const normalizeStatus = (value) => {
  const normalized = String(value || 'Open').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (normalized === 'inprogress' || normalized === 'in_progress') return 'In Progress';
  if (normalized === 'done') return 'Done';
  if (normalized === 'blocked') return 'Blocked';
  if (normalized === 'cancelled' || normalized === 'canceled') return 'Cancelled';
  return 'Open';
};

export const isDone = (task) => normalizeStatus(task?.status) === 'Done';
export const isCancelled = (task) => normalizeStatus(task?.status) === 'Cancelled';
/**
 * Done or Cancelled: no longer pending work. GET /tasks/pending leaves out only Done, so a cancelled task
 * still arrives with the pending ones — it is never counted (focus counters, overdue, Needs details, the
 * Table footer) and leaves the board like a done task; "Show done" shows both, faded.
 */
export const isClosed = (task) => isDone(task) || isCancelled(task);

export const taskAssignees = (task) => {
  if ((task?.assignees || []).length) return task.assignees;
  if (task?.assigned_to_user_id && task?.assigned_to_name) {
    return [{ id: task.assigned_to_user_id, name: task.assigned_to_name, role: '' }];
  }
  return [];
};

export const taskProjectIds = (task) => {
  const ids = (task?.project_ids || []).filter(Boolean);
  if (ids.length) return ids;
  return task?.project_id ? [task.project_id] : [];
};

/** Label the project together with its system, without changing its canonical stored name. */
export const projectLabel = (project) => {
  const name = String(project?.name || project?.id || '').trim();
  const system = String(project?.system_type || '').trim();
  if (!system) return name;
  if (!name) return system;
  const lowerName = name.toLowerCase();
  const lowerSystem = system.toLowerCase();
  return lowerName === lowerSystem || lowerName.endsWith(` — ${lowerSystem}`)
    ? name
    : `${name} — ${system}`;
};

export const taskProjects = (task, projectNameById = {}) => {
  const summaries = (task?.projects || []).filter((project) => project?.id);
  if (summaries.length) {
    return summaries.map((project) => ({
      id: project.id,
      name: projectNameById[project.id] || projectLabel(project) || project.id,
    }));
  }
  return taskProjectIds(task).map((id) => ({ id, name: projectNameById[id] || id }));
};

export const initials = (name) => String(name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

// Avatar fills (white text ≥ 4.5:1 on each). Stable per person: same id → same colour everywhere.
const AVATAR_FILLS = ['#3d3da3', '#0f766e', '#b45309', '#be185d', '#4d7c0f', '#6d28d9'];
export const avatarFill = (key) => {
  const text = String(key || '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  return AVATAR_FILLS[hash % AVATAR_FILLS.length];
};

/** Status → icon key + tone + word. Icons: circle-dashed (Open), circle-dot (In Progress), octagon-alert (Blocked). */
export const STATUS_META = {
  Open: { label: 'Open', icon: 'open', tone: 'gray', iconClass: 'text-ink-3' },
  'In Progress': { label: 'In progress', icon: 'progress', tone: 'blue', iconClass: 'text-status-blue' },
  Blocked: { label: 'Blocked', icon: 'blocked', tone: 'red', iconClass: 'text-status-red' },
  Cancelled: { label: 'Cancelled', icon: 'cancelled', tone: 'outline', iconClass: 'text-ink-3' },
  Done: { label: 'Done', icon: 'done', tone: 'navy', iconClass: 'text-status-navy' },
};
export const statusMeta = (status) => STATUS_META[normalizeStatus(status)] || STATUS_META.Open;

/** Priority → word + text colour from the 'priority' status domain (High red, Medium amber, Low gray). */
export const priorityMeta = (priority) => {
  const label = PRIORITY_OPTIONS.includes(priority) ? priority : 'Medium';
  const tone = getStatusTone(label, 'priority');
  return { label, tone, textClass: TONE_TEXT_CLASS[tone] || TONE_TEXT_CLASS.gray };
};

/**
 * Due date → { kind, label, days }.
 *   kind 'none' | 'overdue' | 'today' | 'tomorrow' | 'later'
 *   'Overdue · 26 Sep' (red, with a word — not just a red date), 'Today' (amber), 'Tomorrow', '8 Oct'.
 * Calendar dates are compared in GST (lib/format), so a task due today is "Today" for everyone.
 */
export const dueInfo = (task, { now } = {}) => {
  const value = task?.due_date;
  const days = value ? ageDays(value, { now }) : null;
  if (days === null || days === undefined) return { kind: 'none', label: 'No date', days: null };
  const date = formatDate(value, { year: 'auto', now });
  if (days > 0) return { kind: 'overdue', label: `Overdue · ${date}`, days, date };
  if (days === 0) return { kind: 'today', label: 'Today', days, date };
  if (days === -1) return { kind: 'tomorrow', label: 'Tomorrow', days, date };
  return { kind: 'later', label: date, days, date };
};

const ORIGIN_TABS = { stage: 'stages', material: 'material', invoicing: 'invoicing' };

/**
 * Where a task came from (tasks created inside a project). null for board-made tasks.
 *   stage     → git-branch  'Technical Submittal'   (stage_name, + line name in the long label)
 *   material  → package     'Material'
 *   invoicing → receipt     'INV-014'               (invoice_reference)
 * `href` opens that part of the project (/projects/:id?tab=…, plus &stage=<id> for stage tasks).
 */
export const taskOrigin = (task) => {
  const source = String(task?.source || '').trim().toLowerCase();
  if (!ORIGIN_TABS[source]) return null;
  const projectId = taskProjectIds(task)[0] || '';
  const href = projectId ? `/projects/${encodeURIComponent(projectId)}?tab=${ORIGIN_TABS[source]}` : '';
  if (source === 'stage') {
    const stage = String(task.stage_name || '').trim();
    const line = String(task.stage_line_name || '').trim();
    const stageId = String(task.stage_id || '').trim();
    const short = stage || 'Stage';
    return {
      kind: 'stage',
      short,
      label: ['Stage', stage, line].filter(Boolean).join(' · '),
      title: `From stage${stage ? ` · ${stage}` : ''}${line ? ` · ${line}` : ''}`,
      // &stage= scrolls the project page to that stage card.
      href: href && stageId ? `${href}&stage=${encodeURIComponent(stageId)}` : href,
    };
  }
  if (source === 'material') {
    return { kind: 'material', short: 'Material', label: 'Material request', title: 'From the project’s Material tab', href };
  }
  const reference = String(task.invoice_reference || '').trim();
  return {
    kind: 'invoicing',
    short: reference || 'Invoice',
    label: reference ? `Invoice · ${reference}` : 'Invoice',
    title: `From invoicing${reference ? ` · ${reference}` : ''}`,
    href,
  };
};

export const sortTasks = (items) => [...items].sort((a, b) => {
  const positionA = Number.isFinite(Number(a.position)) ? Number(a.position) : 0;
  const positionB = Number.isFinite(Number(b.position)) ? Number(b.position) : 0;
  if (positionA !== positionB) return positionA - positionB;
  return String(a.created_at || '').localeCompare(String(b.created_at || ''));
});

const byCompletedDesc = (a, b) => String(b.completed_at || '').localeCompare(String(a.completed_at || ''));

export const sectionOf = (task) => String(task?.section || '').trim() || NO_SECTION;

/**
 * Board lanes. Only sections that have pending work are shown unless `showEmpty`; "No Section" goes last.
 * With `showDone`, this week's done tasks sit (faded) at the bottom of their lane and keep the lane visible.
 * → { lanes: [{ section, tasks, doneTasks, empty }], activeCount, pendingSectionCount, totalCount, emptyCount, allSections }
 *   activeCount  sections with work on screen (pending, or done with showDone) — activeCount + emptyCount = totalCount,
 *                so "4 of 22 sections · Show 18 empty" always adds up to the lanes drawn
 *   pendingSectionCount  sections with pending work only ("N open tasks in M sections")
 */
export const buildLanes = ({
  tasks = [],
  doneTasks = [],
  configuredSections = [],
  showEmpty = false,
  showDone = false,
}) => {
  const pending = new Map();
  tasks.filter((task) => !isClosed(task)).forEach((task) => {
    const section = sectionOf(task);
    pending.set(section, [...(pending.get(section) || []), task]);
  });
  const done = new Map();
  if (showDone) {
    doneTasks.filter(isClosed).forEach((task) => {
      const section = sectionOf(task);
      done.set(section, [...(done.get(section) || []), task]);
    });
  }

  const configured = (configuredSections || []).filter((section) => section && section !== NO_SECTION);
  const extra = [...new Set([...pending.keys(), ...done.keys()])]
    .filter((section) => section !== NO_SECTION && !configured.includes(section))
    .sort((a, b) => a.localeCompare(b));
  const ordered = [...configured, ...extra];
  if (pending.has(NO_SECTION) || done.has(NO_SECTION)) ordered.push(NO_SECTION);

  const hasWork = (section) => pending.has(section) || done.has(section);
  const lanes = ordered
    .filter((section) => showEmpty || hasWork(section))
    .map((section) => ({
      section,
      tasks: sortTasks(pending.get(section) || []),
      doneTasks: [...(done.get(section) || [])].sort(byCompletedDesc),
      empty: !pending.has(section),
    }));

  const withWork = ordered.filter(hasWork).length;
  return {
    lanes,
    activeCount: withWork,
    pendingSectionCount: pending.size,
    totalCount: ordered.length,
    emptyCount: ordered.length - withWork,
    allSections: ordered.filter((section) => section !== NO_SECTION),
  };
};

/**
 * Start of this week (Monday 00:00 in Dubai, GST = UTC+4, no DST) as an ISO instant, for
 * GET /tasks?status=Done&completed_since=… ("Show done · N this week").
 */
export const weekStartIso = (now = new Date()) => {
  const today = toDateInputValue(now instanceof Date ? now : new Date(now)) || toDateInputValue(new Date());
  const [year, month, day] = today.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = Sunday
  const monday = new Date(Date.UTC(year, month - 1, day - ((weekday + 6) % 7)));
  const pad = (value) => String(value).padStart(2, '0');
  return `${monday.getUTCFullYear()}-${pad(monday.getUTCMonth() + 1)}-${pad(monday.getUTCDate())}T00:00:00+04:00`;
};

/** Done since `sinceIso` (client-side guard, so older servers that ignore the filter still give the right list). */
export const completedSince = (task, sinceIso) => {
  if (!isDone(task)) return false;
  const completed = Date.parse(task.completed_at || '');
  const since = Date.parse(sinceIso || '');
  if (!Number.isFinite(completed)) return false;
  return !Number.isFinite(since) || completed >= since;
};

/** 'YYYY-MM-DD' for today + n days in GST (quick picks in the due-date popover). */
export const dateInDays = (days, { now = new Date() } = {}) => {
  const today = toDateInputValue(now);
  const [year, month, day] = today.split('-').map(Number);
  const target = new Date(Date.UTC(year, month - 1, day + days));
  const pad = (value) => String(value).padStart(2, '0');
  return `${target.getUTCFullYear()}-${pad(target.getUTCMonth() + 1)}-${pad(target.getUTCDate())}`;
};

/** Days until next Monday (1–7) — "Next week" quick pick. */
export const daysUntilNextMonday = ({ now = new Date() } = {}) => {
  const [year, month, day] = toDateInputValue(now).split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return ((8 - weekday) % 7) || 7;
};

/**
 * PATCH body for a new due date picked on a card (quick panel, swipe). A multi-day task keeps its start
 * unless the new end would come before it (or the date is cleared) — then it becomes a single-day task,
 * like the bulk bar does; the server refuses a start after the end.
 */
export const dueDatePatch = (task, value) => {
  const start = task?.is_multi_day ? toDateInputValue(task?.start_date) : '';
  if (start && (!value || value < start)) return { due_date: value || '', is_multi_day: false };
  return { due_date: value || '' };
};

/* =====================================================================================================
   Wave 2 (ideas 5 · 6 · 7 · 9): focus counters, filters, Group by, Table durations, Calendar dates.
   ===================================================================================================== */

const PRIORITY_RANK = { High: 0, Medium: 1, Low: 2 };

/** Calendar-date string 'YYYY-MM-DD' for any date value ('' when it is not a date). */
export const isoDate = (value) => toDateInputValue(value) || '';

const pad2 = (value) => String(value).padStart(2, '0');
const isoToUtc = (iso) => {
  const [year, month, day] = String(iso).split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};
const utcToIso = (time) => {
  const date = new Date(time);
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
};
const DAY_MS = 24 * 60 * 60 * 1000;

/** '2026-09-30' + 2 → '2026-10-02' (calendar arithmetic, no time zone involved). */
export const addDaysIso = (iso, days) => utcToIso(isoToUtc(iso) + days * DAY_MS);
/** Whole days from a to b ('2026-09-29' → '2026-10-01' = 2). */
export const daysBetweenIso = (from, to) => Math.round((isoToUtc(to) - isoToUtc(from)) / DAY_MS);
/** Monday = 0 … Sunday = 6. */
export const weekdayIndex = (iso) => (new Date(isoToUtc(iso)).getUTCDay() + 6) % 7;
export const startOfWeekIso = (iso) => addDaysIso(iso, -weekdayIndex(iso));
export const todayIso = ({ now } = {}) => dateInDays(0, { now });

/** Monday … Sunday of the week that holds `anchor`. */
export const weekDays = (anchor) => {
  const monday = startOfWeekIso(anchor);
  return Array.from({ length: 7 }, (_, index) => addDaysIso(monday, index));
};

/** 6 weeks (42 days, Monday first) that cover the month of `anchor`. */
export const monthGrid = (anchor) => {
  const first = `${String(anchor).slice(0, 7)}-01`;
  const start = startOfWeekIso(first);
  return Array.from({ length: 42 }, (_, index) => addDaysIso(start, index));
};

/** Working days (Mon–Fri) from start to end, both included. */
export const workingDaysBetween = (start, end) => {
  if (!start || !end || end < start) return 0;
  let count = 0;
  for (let day = start, guard = 0; day <= end && guard < 3660; day = addDaysIso(day, 1), guard += 1) {
    if (weekdayIndex(day) < 5) count += 1;
  }
  return count;
};

/**
 * The days a task covers on a calendar: multi-day tasks from start_date to due_date, others on their due
 * date. null when it has no (valid) due date.
 */
export const taskSpan = (task) => {
  const end = isoDate(task?.due_date);
  if (!end) return null;
  const start = task?.is_multi_day ? isoDate(task?.start_date) : '';
  if (start && start < end) return { start, end, multi: true };
  return { start: end, end, multi: false };
};

/**
 * Multi-day progress for the Table's "Duration / Progress" column.
 * → null (single-day task) | { total, progress 0–100, label, tone }
 */
export const durationInfo = (task, { now } = {}) => {
  const span = taskSpan(task);
  if (!span?.multi) return null;
  const total = Math.max(1, workingDaysBetween(span.start, span.end));
  const today = todayIso({ now });
  if (isDone(task)) return { total, progress: 100, label: 'Finished', tone: 'navy' };
  if (isCancelled(task)) return { total, progress: 0, label: 'Cancelled', tone: 'gray' };
  if (today < span.start) {
    const days = daysBetweenIso(today, span.start);
    return { total, progress: 0, label: days === 1 ? 'Starts tomorrow' : `Starts in ${days} d`, tone: 'gray' };
  }
  if (today > span.end) return { total, progress: 100, label: `Ended ${formatDate(span.end, { year: 'auto', now })}`, tone: 'red' };
  const elapsed = Math.min(total, Math.max(1, workingDaysBetween(span.start, today)));
  return { total, progress: Math.round((elapsed / total) * 100), label: `Day ${elapsed} of ${total}`, tone: 'blue' };
};

/* ---- who / what ---- */

/** True when `user` is one of the task's people (by id; by name for people added before ids existed). */
export const isAssignedTo = (task, user) => {
  if (!user) return false;
  const id = String(user.id || user.sub || '');
  const name = String(user.name || '').trim().toLowerCase();
  return taskAssignees(task).some((assignee) => (
    (id && assignee.id === id) || (name && String(assignee.name || '').trim().toLowerCase() === name)
  ));
};

export const isBlocked = (task) => normalizeStatus(task?.status) === 'Blocked';

/** No project, no person and no date — e.g. the old "Untitled task"s. They wait in the Needs details card. */
export const needsDetails = (task) => (
  !isClosed(task)
  && !taskProjectIds(task).length
  && !taskAssignees(task).length
  && !isoDate(task?.due_date)
);

/** Soonest due first; no date last; then High before Low; then by name. */
export const sortByDue = (items) => [...items].sort((a, b) => {
  const dueA = isoDate(a.due_date) || '9999-12-31';
  const dueB = isoDate(b.due_date) || '9999-12-31';
  if (dueA !== dueB) return dueA < dueB ? -1 : 1;
  const rankA = PRIORITY_RANK[priorityMeta(a.priority).label];
  const rankB = PRIORITY_RANK[priorityMeta(b.priority).label];
  if (rankA !== rankB) return rankA - rankB;
  return String(a.name || '').localeCompare(String(b.name || ''));
});

/* ---- focus counters (idea 5) ---- */

export const FOCUS_OPTIONS = [
  { key: 'mine', label: 'Assigned to me', short: 'Mine', tone: 'navy' },
  { key: 'today', label: 'Due today', short: 'Today', tone: 'amber' },
  { key: 'overdue', label: 'Overdue', short: 'Overdue', tone: 'red' },
  { key: 'blocked', label: 'Blocked', short: 'Blocked', tone: 'red' },
  { key: 'details', label: 'Needs details', short: 'Needs details', tone: 'amber' },
];
export const FOCUS_KEYS = FOCUS_OPTIONS.map((option) => option.key);

/** `fresh` = ids created in this visit: they stay in their lane until the next visit (see the board). */
export const matchesFocus = (task, focus, { user, now, fresh } = {}) => {
  switch (focus) {
    case 'mine': return isAssignedTo(task, user); // also picks this week's done tasks for "Show done"
    case 'today': return !isClosed(task) && dueInfo(task, { now }).kind === 'today';
    case 'overdue': return !isClosed(task) && dueInfo(task, { now }).kind === 'overdue';
    case 'blocked': return !isClosed(task) && isBlocked(task);
    case 'details': return needsDetails(task) && !fresh?.has(task.id);
    default: return true;
  }
};

/**
 * A counter's meta line as `{ meta, tail? }`: `meta` is the whole line (tooltip, screen readers); `tail` is its
 * end that must stay visible when the name is cut (" · 5 Oct", " +2") — only there when there is one.
 */
const metaLine = (lead, tail = '') => (tail ? { meta: `${lead}${tail}`, tail } : { meta: lead });
const plusMore = (first, count) => metaLine(first, count > 1 ? ` +${count - 1}` : '');
const sentence = (text) => {
  const clean = String(text || '').trim();
  return clean ? clean[0].toUpperCase() + clean.slice(1) : '';
};

/**
 * Names in a short line, repeats collapsed: ['dsfeg', 'Untitled task' ×3] → "dsfeg, 3× Untitled task".
 * Keeps the order of first appearance; past `limit` distinct names → "… +N more". Blank names = "Untitled task".
 */
export const summarizeNames = (names, { limit = 3 } = {}) => {
  const counts = new Map();
  (names || []).forEach((raw) => {
    const name = String(raw || '').trim() || 'Untitled task';
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  const entries = [...counts.entries()];
  const shown = entries.slice(0, limit).map(([name, count]) => (count > 1 ? `${count}× ${name}` : name));
  const rest = entries.slice(limit).reduce((sum, [, count]) => sum + count, 0);
  return `${shown.join(', ')}${rest ? ` +${rest} more` : ''}`;
};

/**
 * Counts + one line of context for each focus counter (`tail` = the end of the line that must not be cut):
 *   mine     2  "Next: Order GRMS controllers · 2 Oct"
 *   today    3  "Sequence of operation document +2"
 *   overdue  2  "Oldest 4 days late"
 *   blocked  1  "Waiting LPO approval from client"
 *   details  4  "No project, person or date"
 */
export const focusSummary = (tasks, { user, now, fresh } = {}) => {
  const pending = tasks.filter((task) => !isClosed(task));
  const of = (key) => pending.filter((task) => matchesFocus(task, key, { user, now, fresh }));
  const mine = sortByDue(of('mine'));
  const today = sortByDue(of('today'));
  const overdue = of('overdue');
  const blocked = sortByDue(of('blocked'));
  const details = of('details');
  const oldest = overdue.reduce((max, task) => Math.max(max, dueInfo(task, { now }).days || 0), 0);
  const next = mine.find((task) => isoDate(task.due_date)) || mine[0];
  const firstReason = blocked.find((task) => String(task.blocked_reason || '').trim());
  return {
    mine: {
      count: mine.length,
      ...(next
        ? metaLine(`Next: ${next.name}`, isoDate(next.due_date) ? ` · ${formatDate(next.due_date, { year: 'auto', now })}` : '')
        : { meta: 'Nothing assigned to you' }),
    },
    today: { count: today.length, ...(today.length ? plusMore(today[0].name, today.length) : { meta: 'Nothing due today' }) },
    overdue: {
      count: overdue.length,
      meta: overdue.length ? `Oldest ${oldest} ${oldest === 1 ? 'day' : 'days'} late` : 'Nothing late',
    },
    blocked: {
      count: blocked.length,
      ...(blocked.length
        ? plusMore(firstReason ? sentence(firstReason.blocked_reason) : 'No reason given yet', blocked.length)
        : { meta: 'Nothing blocked' }),
    },
    details: { count: details.length, meta: details.length ? 'No project, person or date' : 'Every task has its details' },
  };
};

/* ---- filter chips (idea 5) ---- */

export const NONE_VALUE = 'none';

const matchesAny = (selected, values) => {
  if (!selected?.length) return true;
  if (!values.length) return selected.includes(NONE_VALUE);
  return values.some((value) => selected.includes(value));
};

/** Person / Project / Priority / Location chips (each a list; 'none' = Unassigned / No project / No location). */
export const applyTaskFilters = (tasks, { person = [], project = [], priority = [], location = [] } = {}) => tasks.filter((task) => (
  matchesAny(person, taskAssignees(task).map((assignee) => assignee.id))
  && matchesAny(project, taskProjectIds(task))
  && (!priority.length || priority.includes(priorityMeta(task.priority).label))
  && matchesAny(location, task.locations || [])
));

const countBy = (tasks, valuesOf) => {
  const counts = new Map();
  let none = 0;
  tasks.forEach((task) => {
    const values = [...new Set(valuesOf(task))];
    if (!values.length) none += 1;
    values.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
  });
  return { counts, none };
};

/**
 * Options for the filter chips, with counts over `tasks`. People and projects only list those that have
 * tasks (plus anything already picked), so the pickers stay short.
 */
export const buildFilterOptions = (tasks, {
  members = [],
  projectNameById = {},
  locations = [],
  selected = {},
} = {}) => {
  const memberName = new Map(members.map((member) => [member.id, member.name]));
  const personNames = new Map();
  tasks.forEach((task) => taskAssignees(task).forEach((assignee) => personNames.set(assignee.id, assignee.name)));
  const people = countBy(tasks, (task) => taskAssignees(task).map((assignee) => assignee.id));
  const personIds = new Set([...people.counts.keys(), ...(selected.person || []).filter((id) => id !== NONE_VALUE)]);
  const person = [...personIds]
    .map((id) => ({ value: id, label: memberName.get(id) || personNames.get(id) || id, count: people.counts.get(id) || 0 }))
    .sort((a, b) => a.label.localeCompare(b.label));
  person.push({ value: NONE_VALUE, label: 'Unassigned', count: people.none, muted: true });

  const projectNames = new Map();
  tasks.forEach((task) => taskProjects(task, projectNameById).forEach((item) => projectNames.set(item.id, item.name)));
  const projectCounts = countBy(tasks, taskProjectIds);
  const projectIds = new Set([...projectCounts.counts.keys(), ...(selected.project || []).filter((id) => id !== NONE_VALUE)]);
  const project = [...projectIds]
    .map((id) => ({ value: id, label: projectNameById[id] || projectNames.get(id) || id, count: projectCounts.counts.get(id) || 0 }))
    .sort((a, b) => a.label.localeCompare(b.label));
  project.push({ value: NONE_VALUE, label: 'No project', count: projectCounts.none, muted: true });

  const priorities = countBy(tasks, (task) => [priorityMeta(task.priority).label]);
  const priority = PRIORITY_OPTIONS.map((value) => ({ value, label: value, count: priorities.counts.get(value) || 0 }));

  const locationCounts = countBy(tasks, (task) => task.locations || []);
  const location = [...new Set([...locations, ...locationCounts.counts.keys()])]
    .map((value) => ({ value, label: value, count: locationCounts.counts.get(value) || 0 }));
  location.push({ value: NONE_VALUE, label: 'No location', count: locationCounts.none, muted: true });

  return { person, project, priority, location };
};

/* ---- Group by (idea 5) ---- */

export const GROUP_OPTIONS = [
  { value: 'section', label: 'Section' },
  { value: 'person', label: 'Person' },
  { value: 'project', label: 'Project' },
  { value: 'due', label: 'Due date' },
];
export const GROUP_KEYS = GROUP_OPTIONS.map((option) => option.value);

export const DUE_GROUPS = [
  { key: 'overdue', label: 'Overdue', tone: 'red' },
  { key: 'today', label: 'Today', tone: 'amber' },
  { key: 'blocked', label: 'Blocked', tone: 'red' },
  { key: 'later', label: 'Later', tone: 'gray' },
  { key: 'none', label: 'No date', tone: 'gray' },
  { key: 'closed', label: 'Done & cancelled', tone: 'gray' }, // only with "Show done"
];

/** Blocked wins (it says why the task waits), then overdue / today / later / no date. Done / cancelled: 'closed'. */
export const dueGroupOf = (task, { now } = {}) => {
  if (isClosed(task)) return 'closed';
  if (isBlocked(task)) return 'blocked';
  const kind = dueInfo(task, { now }).kind;
  if (kind === 'overdue' || kind === 'today' || kind === 'none') return kind;
  return 'later';
};

const pushTo = (map, key, task) => map.set(key, [...(map.get(key) || []), task]);

/**
 * Lanes for Person / Project / Due date grouping (Section grouping is buildLanes). A task with two people
 * (or projects) shows in both lanes. Lanes without tasks are left out; "Unassigned" / "No project" last.
 * → [{ key, label, tasks, doneTasks, tone?, prefill }]   prefill = what "Add task" in that lane fills in
 */
export const groupTasks = (tasks, group, { doneTasks = [], showDone = false, projectNameById = {}, now } = {}) => {
  const lanes = new Map();
  const doneLanes = new Map();
  const labels = new Map();
  const place = (task, target) => {
    if (group === 'person') {
      const people = taskAssignees(task);
      if (!people.length) pushTo(target, NONE_VALUE, task);
      people.forEach((person) => { labels.set(person.id, person.name); pushTo(target, person.id, task); });
    } else if (group === 'project') {
      const projects = taskProjects(task, projectNameById);
      if (!projects.length) pushTo(target, NONE_VALUE, task);
      projects.forEach((project) => { labels.set(project.id, project.name); pushTo(target, project.id, task); });
    } else {
      pushTo(target, dueGroupOf(task, { now }), task);
    }
  };
  tasks.filter((task) => !isClosed(task)).forEach((task) => place(task, lanes));
  if (showDone) doneTasks.filter(isClosed).forEach((task) => place(task, doneLanes));

  if (group === 'due') {
    return DUE_GROUPS
      .filter((item) => lanes.has(item.key) || doneLanes.has(item.key))
      .map((item) => ({
        key: item.key,
        label: item.label,
        tone: item.tone,
        tasks: sortByDue(lanes.get(item.key) || []),
        doneTasks: [...(doneLanes.get(item.key) || [])].sort(byCompletedDesc),
        prefill: item.key === 'today' ? { due_date: dateInDays(0, { now }) } : null,
      }));
  }
  const noneLabel = group === 'person' ? 'Unassigned' : 'No project';
  return [...new Set([...lanes.keys(), ...doneLanes.keys()])]
    .sort((a, b) => {
      if (a === NONE_VALUE) return 1;
      if (b === NONE_VALUE) return -1;
      return String(labels.get(a) || a).localeCompare(String(labels.get(b) || b));
    })
    .map((key) => ({
      key,
      label: key === NONE_VALUE ? noneLabel : (labels.get(key) || key),
      tone: key === NONE_VALUE ? 'amber' : undefined,
      tasks: sortByDue(lanes.get(key) || []),
      doneTasks: [...(doneLanes.get(key) || [])].sort(byCompletedDesc),
      prefill: key === NONE_VALUE ? null : group === 'person'
        ? { assignee: { id: key, name: labels.get(key) || key } }
        : { project: { id: key, name: labels.get(key) || key } },
    }));
};

/* =====================================================================================================
   Wave 3 (idea 10): Team load — who carries what, and how much of the team's work closes on time.
   ===================================================================================================== */

export const TEAM_PERIODS = [
  { key: 'this', label: 'This week' },
  { key: 'next', label: 'Next week' },
];

/** Monday … Sunday (GST calendar days) of this week (offset 0) or a later / earlier one. */
export const weekBounds = (offset = 0, { now } = {}) => {
  const start = addDaysIso(startOfWeekIso(todayIso({ now })), offset * 7);
  return { start, end: addDaysIso(start, 6) };
};

/**
 * The colour block an open task adds to a person's load bar:
 *   'red'      blocked, or overdue (needs attention first — wins over In progress)
 *   'progress' in progress
 *   'open'     not started
 */
export const loadKind = (task, { now } = {}) => {
  if (isBlocked(task) || dueInfo(task, { now }).kind === 'overdue') return 'red';
  return normalizeStatus(task?.status) === 'In Progress' ? 'progress' : 'open';
};

/** The GST calendar day a done task was closed ('' when it has no completion time). */
export const completedDay = (task) => (isDone(task) ? isoDate(task?.completed_at) : '');

const emptyLoad = () => ({ tasks: [], open: 0, progress: 0, plain: 0, red: 0, late: 0, blocked: 0 });
const addToLoad = (load, task, now) => {
  load.tasks.push(task);
  load.open += 1;
  const kind = loadKind(task, { now });
  if (kind === 'red') load.red += 1;
  else if (kind === 'progress') load.progress += 1;
  else load.plain += 1;
  if (isBlocked(task)) load.blocked += 1;
  else if (dueInfo(task, { now }).kind === 'overdue') load.late += 1;
};

/**
 * Team load rows for the Team load card / view.
 *   tasks       pending tasks on the board (the page leaves out the "Needs details" ones: they are counted
 *               apart, as `needsDetails`, on the Unassigned row)
 *   doneTasks   done tasks (this week's are counted per person as "Done N")
 *   members     /tasks/team-members (names and roles)
 *   period      'this' = everything a person has open now + what they closed since Monday;
 *               'next' = open tasks due next week (Mon–Sun), to plan ahead
 * → { people: [{ id, name, role, tasks, open, progress, plain, red, late, blocked, done }], unassigned,
 *     scale (blocks the widest bar is measured against, at least 4), range: { start, end } }
 * People are sorted by open work (most first), then by what needs attention, then by name. Everyone with
 * open work is listed (for 'next' with 0 when nothing of theirs is due next week); someone with nothing open
 * only when they closed something this week.
 */
export const teamLoad = (tasks = [], {
  doneTasks = [],
  members = [],
  period = 'this',
  needsDetails: needsDetailsCount = 0,
  now,
} = {}) => {
  const range = weekBounds(period === 'next' ? 1 : 0, { now });
  const inPeriod = (task) => {
    if (period !== 'next') return true;
    const due = isoDate(task.due_date);
    return Boolean(due) && due >= range.start && due <= range.end;
  };
  const memberById = new Map(members.map((member) => [member.id, member]));
  const people = new Map();
  const personRow = (assignee) => {
    const id = assignee.id || assignee.name;
    if (!people.has(id)) {
      const member = memberById.get(id);
      people.set(id, {
        id,
        name: member?.name || assignee.name || 'Unknown person',
        role: member?.role || assignee.role || '',
        ...emptyLoad(),
        done: 0,
      });
    }
    return people.get(id);
  };
  const unassigned = { ...emptyLoad(), needsDetails: needsDetailsCount };

  // Everyone with open work gets a row — for "Next week" too (Due 0 = room to take more).
  tasks.filter((task) => !isClosed(task)).forEach((task) => {
    const counted = inPeriod(task);
    const assignees = taskAssignees(task);
    if (!assignees.length && counted) addToLoad(unassigned, task, now);
    assignees.forEach((assignee) => {
      const row = personRow(assignee);
      if (counted) addToLoad(row, task, now);
    });
  });
  if (period !== 'next') {
    doneTasks.forEach((task) => {
      const day = completedDay(task);
      if (!day || day < range.start || day > range.end) return;
      taskAssignees(task).forEach((assignee) => { personRow(assignee).done += 1; });
    });
  }

  const rows = [...people.values()]
    .map((row) => ({ ...row, tasks: sortByDue(row.tasks) }))
    .sort((a, b) => (b.open - a.open) || (b.red - a.red) || a.name.localeCompare(b.name));
  unassigned.tasks = sortByDue(unassigned.tasks);
  const widest = Math.max(0, ...rows.map((row) => row.open), unassigned.open);
  return { people: rows, unassigned, scale: Math.max(4, widest), range };
};

/**
 * On-time completion per week (idea 10's chart): tasks closed in each of the last `weeks` weeks (the current
 * week last) that had a due date, and how many of them closed on or before it. Tasks without a due date
 * cannot be late, so they are left out of the rate (but counted in `closed`).
 * → [{ start, end, closed, dated, onTime, rate (0–100) | null when nothing dated closed that week }]
 */
export const onTimeWeeks = (doneTasks = [], { weeks = 8, now } = {}) => {
  const thisWeek = weekBounds(0, { now });
  const seen = new Set();
  const unique = doneTasks.filter((task) => {
    if (!task?.id || seen.has(task.id)) return false;
    seen.add(task.id);
    return true;
  });
  return Array.from({ length: weeks }, (_, index) => {
    const start = addDaysIso(thisWeek.start, -(weeks - 1 - index) * 7);
    const end = addDaysIso(start, 6);
    const closed = unique.filter((task) => {
      const day = completedDay(task);
      return day && day >= start && day <= end;
    });
    const dated = closed.filter((task) => isoDate(task.due_date));
    const onTime = dated.filter((task) => completedDay(task) <= isoDate(task.due_date));
    return {
      start,
      end,
      closed: closed.length,
      dated: dated.length,
      onTime: onTime.length,
      rate: dated.length ? Math.round((onTime.length / dated.length) * 100) : null,
    };
  });
};

/**
 * Assignee change for "drop a card on a person": add the person; when the card was dragged out of another
 * person's row (Team view), that person is taken off — a reassignment, not a second owner.
 * → null when nothing would change (already assigned to them).
 */
export const assignPatch = (task, person, { fromPersonId } = {}) => {
  const current = taskAssignees(task);
  const kept = current.filter((assignee) => !fromPersonId || assignee.id !== fromPersonId || assignee.id === person.id);
  if (kept.some((assignee) => assignee.id === person.id)) {
    return kept.length === current.length ? null : { assignee_ids: kept.map((item) => item.id), assignees: kept };
  }
  const next = [...kept, { id: person.id, name: person.name, role: person.role || '' }];
  return { assignee_ids: next.map((item) => item.id), assignees: next };
};
